import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Container, 
  CssBaseline, 
  Box,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import BroadcastPage from './components/BroadcastPage';
import StreamingPage from './components/StreamingPage';
import HomePage from './components/HomePage';
import AccountPage from './components/AccountPage';
import FeaturesPage from './components/FeaturesPage';
import LoginButton from './components/Loginbutton';
import LogoutButton from './components/LogoutButton';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth0 } from '@auth0/auth0-react';
import { CustomersProvider } from './contexts/CustomersContext';
import { Footer } from './components/ui';
import { API_URL } from './config';
import OrganizationNameForm from './components/OrganizationNameForm';

// Success and Cancel components
const SubscriptionSuccess = () => {
  const [customerId, setCustomerId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const location = useLocation();
  const { user, loginWithRedirect } = useAuth0();
  
  useEffect(() => {
    // Extract session_id from URL query parameters
    const params = new URLSearchParams(location.search);
    const session = params.get('session_id');
    
    if (session) {
      console.log('Found session_id in URL:', session);
      fetchSessionDetails(session);
      
      // Google Ads Conversion Tracking
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11199925755/f0EeCNeV-bUaEPubxdwp'
        });
        console.log('Google Ads conversion event triggered');
      }
    } else {
      console.log('No session_id found in URL');
    }
  }, [location]);
  
  const fetchSessionDetails = async (session) => {
    try {
      setIsLoading(true);
      setError('');
      
      console.log('Fetching session details for session:', session);
      const response = await fetch(`${API_URL}/api/checkout-success?session_id=${session}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch session details');
      }
      
      const data = await response.json();
      console.log('Session details received:', data);
      setCustomerId(data.customerId);
      
    } catch (err) {
      console.error('Error fetching session details:', err);
      setError('Failed to load session details. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleOrgNameSuccess = (newName) => {
    setSuccess(true);
    // Redirect to login after a short delay
    setTimeout(() => {
      loginWithRedirect();
    }, 3000);
  };
  
  if (isLoading && !customerId) {
    return (
      <Container>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
          <Typography variant="h4" gutterBottom>Processing your subscription...</Typography>
          <CircularProgress size={60} sx={{ mt: 4 }} />
        </Box>
      </Container>
    );
  }
  
  if (error && !customerId) {
    return (
      <Container>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
          <Typography variant="h4" gutterBottom>Something went wrong</Typography>
          <Alert severity="error" sx={{ mt: 2, mb: 4 }}>{error}</Alert>
          <Button variant="contained" component={Link} to="/" sx={{ mt: 2 }}>
            Return to Home
          </Button>
        </Box>
      </Container>
    );
  }
  
  if (success) {
    return (
      <Container>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
          <Typography variant="h4" gutterBottom>Thank you!</Typography>
          <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>Your organization name has been saved.</Typography>
          <Typography variant="body1">You will be redirected to login in a moment...</Typography>
          <CircularProgress size={30} sx={{ mt: 4 }} />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <Typography variant="h4" gutterBottom>Subscription Successful!</Typography>
        <Typography paragraph>Thank you for subscribing to Glossa.link Broadcasting.</Typography>
        
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%' }}>
          <Typography variant="h5" gutterBottom>Organization Setup</Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Please set your organization name.</strong> This name will be visible to users when they search for broadcasts.
              You can update this name at any time from the Account page.
            </Typography>
          </Alert>
          
          <OrganizationNameForm 
            currentOrganizationName=""
            onSuccess={handleOrgNameSuccess}
            customerId={customerId}
            userEmail={user?.email}
          />
        </Paper>
      </Box>
    </Container>
  );
};

const SubscriptionCancel = () => (
  <Container>
    <Typography variant="h4" gutterBottom>
      Subscription Cancelled
    </Typography>
    <Typography paragraph>
      The subscription process was cancelled. You can try again when you're ready.
    </Typography>
    <Button variant="contained" component={Link} to="/broadcast">
      Return to Broadcast
    </Button>
  </Container>
);

function App() {
  const { isAuthenticated } = useAuth0();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const navItems = [
    { text: 'Features & Benefits', path: '/features' },
    { text: 'Stream', path: '/stream' },
    ...(isAuthenticated ? [{ text: 'Broadcast', path: '/broadcast' }] : []),
    ...(isAuthenticated ? [{ text: 'Account', path: '/account' }] : [])
  ];

  const mobileDrawer = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        <ListItem>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Glossa.link
          </Typography>
        </ListItem>
        <Divider />
        {navItems.map((item) => (
          <ListItem button key={item.text} component={Link} to={item.path}>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem>
          {isAuthenticated ? <LogoutButton fullWidth /> : <LoginButton fullWidth />}
        </ListItem>
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: '100vh' 
        }}>
          <AppBar position="static" elevation={2} color="primary">
            <Toolbar>
              <Typography 
                variant="h6" 
                component={Link} 
                to="/" 
                sx={{ 
                  flexGrow: 1, 
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': {
                    opacity: 0.9,
                    cursor: 'pointer'
                  }
                }}
              >
                Glossa.link
              </Typography>
              
              {isMobile ? (
                <>
                  <IconButton
                    size="large"
                    edge="end"
                    color="inherit"
                    aria-label="menu"
                    onClick={toggleDrawer(true)}
                  >
                    <MenuIcon />
                  </IconButton>
                  <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={toggleDrawer(false)}
                  >
                    {mobileDrawer}
                  </Drawer>
                </>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button color="inherit" component={Link} to="/features">
                    Features & Benefits
                  </Button>
                  <Button color="inherit" component={Link} to="/stream">
                    Stream
                  </Button>
                  {isAuthenticated && (
                    <Button color="inherit" component={Link} to="/broadcast">
                      Broadcast
                    </Button>
                  )}
                  {isAuthenticated && (
                    <Button color="inherit" component={Link} to="/account">
                      Account
                    </Button>
                  )}
                  <LoginButton />
                  <LogoutButton />
                </Box>
              )}
            </Toolbar>
          </AppBar>
          
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
            <CustomersProvider>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route 
                  path="/broadcast" 
                  element={
                    <ProtectedRoute>
                      <BroadcastPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/account" 
                  element={
                    <ProtectedRoute>
                      <AccountPage />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/stream" element={<StreamingPage />} />
                <Route path="/stream/:customerId" element={<StreamingPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/success" element={<SubscriptionSuccess />} />
                <Route path="/cancel" element={<SubscriptionCancel />} />
              </Routes>
            </CustomersProvider>
          </Container>
          
          <Footer />
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
